import { CardConfig } from '@affinis/smartus-components/lib/card/card.component.types';
import {
  Component,
  inject,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TokenClaims } from '@azure/msal-common';
import {
  ContactViewModel,
  EmploymentSearchResultDto,
  IncidentIdResult,
  RoleClient,
  RoleIncidentRequest,
  RoleViewModel,
  SmartPortalProcessError,
  UserAdministrationClient,
  UserClient,
  UserViewModel,
} from '@core/api';
import { environment } from '@core/environments/environment';
import { BreadcrumbService } from '@core/services/breadcrumb-service/BreadcrumbService';
import { ContactSearchFormlyFieldService } from '@core/services/contact-search-formly-field-service/contact-search-formly-field.service';
import { EmployeeSearchFormlyFieldService } from '@core/services/employee-search-formly-field-service/employee-search-formly-field.service';
import { UserService } from '@core/services/oauth-service/user.service';
import { TicketCreateBaseComponent } from '@features/tickets/components/ticket-create/ticket-create.base';
import { MtxGridColumn } from '@ng-matero/extensions/grid';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { sameArrayMembers } from '@shared/utils/comparer';
import { RegexpEmail } from '@shared/utils/regex';
import { UserRoles } from '@shared/constants/user-roles.constants';
import {
  isRoleExpired,
  isRoleExtendable,
  toDateOnlyString,
  toRoleValidityRequests,
} from '@shared/utils/role-validity.utils';
import moment from 'moment';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  AssignUserRoleComponent,
  AssignUserRoleDialogResult,
} from './assign-user-role/assign-user-role.component';
import { EndUserRoleComponent } from './end-user-role/end-user-role.component';
import { ExtendUserRoleComponent } from './extend-user-role/extend-user-role.component';

export interface UserRoleAssignmentRow {
  id: string;
  employeeId: string;
  // Id des Nutzers im Verzeichnis (Azure AD B2C). Wird vom Backend als
  // B2cUserId fuer ExtendRole/EndRole erwartet und ist nicht die
  // Personalnummer aus employeeId.
  b2cUserId: string;
  username: string;
  email: string;
  roleId: string;
  roleName: string;
  validFrom: Date;
  validUntil: Date;
  // Bereits abgelaufene Vergabe: wird rot angezeigt und laesst sich nur noch
  // verlaengern, nicht mehr beenden.
  isExpired?: boolean;
}

/**
 * Bringt einen Treffer beider Personensuchen auf dieselbe Form. Die
 * Mitarbeitersuche liefert ein EmploymentSearchResultDto mit fullName und
 * Personalnummer, die Kontaktsuche ein ContactViewModel mit getrennten Namen,
 * contactId und telephone.
 */
function toEmployee(
  person: EmploymentSearchResultDto | ContactViewModel
): EmploymentSearchResultDto {
  if (!person) {
    return null;
  }

  const employment = person as EmploymentSearchResultDto;
  const contact = person as ContactViewModel;

  return {
    id: employment.id ?? contact.contactId,
    firstName: person.firstName,
    lastName: person.lastName,
    fullName:
      employment.fullName ??
      [person.firstName, person.lastName].filter(Boolean).join(' '),
    email: person.email,
    phone: employment.phone ?? contact.telephone,
    mobilePhone: employment.mobilePhone,
    company: employment.company ?? contact.organisation?.name,
    orgUnit: employment.orgUnit,
  };
}

@Component({
  selector: 'app-user-permissions',
  templateUrl: './user-permissions.component.html',
  styleUrls: ['./user-permissions.component.scss'],
  standalone: false,
})
export class UserPermissionsComponent
  extends TicketCreateBaseComponent
  implements OnInit
{
  headline = this.translateService.instant(
    'menu.user_administration.component.user_role'
  );

  // Umschalter zwischen der Dropdown-Variante und der Variante mit Button,
  // Modal und Rollen-Tabelle. Wird über environment.useLegacyUserPermissionsUi
  // gesteuert, damit beide Varianten erhalten bleiben.
  useLegacyUi = environment.useLegacyUserPermissionsUi ?? true;

  isRequestForOwnUser = true;

  currentUserTokenClaims: TokenClaims & {
    [key: string]: unknown;
  };

  private rolesSubject = new BehaviorSubject<RoleViewModel[]>([]);
  roles$ = this.rolesSubject.asObservable();

  isTelekomUser = false;

  currentUser: UserViewModel;

  private readonly breadcrumbService = inject(BreadcrumbService);
  private readonly roleClient = inject(RoleClient);
  private readonly userClient = inject(UserClient);
  private readonly userService = inject(UserService);
  private readonly userAdministrationClient = inject(UserAdministrationClient);
  private readonly toastr = inject(ToastrService);
  private readonly employeeFormlyFieldService = inject(
    EmployeeSearchFormlyFieldService
  );
  private readonly contactFormlyFieldService = inject(
    ContactSearchFormlyFieldService
  );
  private readonly roleAssignmentDialog = inject(MatDialog);

  /** Verlaengern wird erst kurz vor Ablauf der Vergabe angeboten. */
  readonly isRoleExtendable = isRoleExtendable;

  // Das Backend erlaubt ExtendRole und UpdateUser nur mit der Admin-Rolle der
  // Benutzeradministration, die Seite selbst schon mit roleadministration.
  readonly canManageRoleAssignments = (
    this.userService.getCurrentUserRoles() ?? []
  ).includes(UserRoles.SMARTPORTAL_ADMIN_USER_ADMINISTRATION);

  // Vollstaendiger Verzeichnis-Eintrag des ausgewaehlten Nutzers. Das Beenden
  // speichert den Nutzer ohne die Rolle und braucht dafuer Name, Kontakt und
  // die uebrigen Rollen samt Gueltigkeit.
  private selectedUser: UserViewModel;

  @ViewChild('extendTemplate', { static: true })
  extendTemplateRef!: TemplateRef<void>;

  @ViewChild('endTemplate', { static: true })
  endTemplateRef!: TemplateRef<void>;

  selectedEmployeeId: string;

  selectedEmployee: EmploymentSearchResultDto;

  // Id des ausgewaehlten Nutzers im Verzeichnis. Die Personensuche liefert nur
  // die Personalnummer, deshalb wird der Nutzer ueber seine E-Mail-Adresse
  // nachgeladen und die Verzeichnis-Id separat gehalten.
  selectedB2cUserId: string;

  // Telekom-Mitarbeitende bekommen ihre Rollen unbefristet: fuer sie entfallen
  // der Gueltigkeitszeitraum im Zuweisungsdialog und die Spalten "Gueltig von",
  // "Gueltig bis" und "Hinzufuegen/Verlaengern". Massgeblich ist der
  // ausgewaehlte Nutzer, nicht der angemeldete. Solange zu ihm kein
  // Verzeichnis-Eintrag geladen ist, bleibt der Zeitraum sichtbar.
  selectedUserIsTelekomEmployee = false;

  // Filter der Personensuche. Telekom-Mitarbeitende kommen aus der
  // Mitarbeitersuche (SearchEmployee), Externe aus der Kontaktsuche
  // (Contact/search). Beide Quellen liefern unterschiedlich geformte Treffer,
  // die in toEmployee vereinheitlicht werden.
  isTelekomPersonSearch = false;

  assignedRoles: UserRoleAssignmentRow[] = [];

  assignedRolesColumns: MtxGridColumn[];

  // --- nur Dropdown-Variante (useLegacyUi === true) ---
  currentRoles: RoleViewModel[];

  legacyCardUser: CardConfig = {
    header: {
      title: {
        label: this.translateService.instant('user.request.permissions.title'),
      },
    },
    footer: {
      button1: {
        label: this.translateService.instant(
          'user.request.permissions.label__submit'
        ),
        onClick: () => this.validateForm(),
        disabled: false,
      },
      button2: {
        label: this.translateService.instant(
          'user.request.permissions.label__reset'
        ),
        onClick: () => this.reset(),
        disabled: false,
      },
    },
  };

  override successMessage = this.translateService.instant(
    'user.request.permissions.successMessage'
  );

  ngOnInit(): void {
    this.loading = true;
    this.roleClient.get().subscribe({
      next: roles => {
        this.rolesSubject.next(roles);
      },
    });

    this.breadcrumbService.setBreadcrumbs([{ label: this.headline }]);

    this.currentUserTokenClaims = this.userService.getCurrentUserTokenClaims();

    this.isTelekomUser =
      this.currentUserTokenClaims.ciamId &&
      this.currentUserTokenClaims.ciamId !== '';

    // Wie in der Benutzeranlage: der Filter startet auf der Gruppe, der die
    // anmeldende Person selbst angehoert.
    this.isTelekomPersonSearch = !!this.isTelekomUser;

    this.userClient.getOwnUser().subscribe({
      next: res => {
        this.currentUser = res;

        if (this.useLegacyUi) {
          this.currentRoles = res.roles;
          this.initLegacyFormFields();
        } else {
          this.onToggleChange();
        }

        this.loading = false;
      },
      error: () => {
        this.loading = false;
      },
    });

    if (!this.useLegacyUi) {
      this.initAssignedRolesColumns();
    }
  }

  override getStatusPath() {
    return '/dashboard';
  }

  // ===========================================================================
  // Dropdown-Variante (useLegacyUi === true)
  // ===========================================================================

  private initLegacyFormFields(): void {
    if (!this.isTelekomUser) {
      this.formFields = this.getLegacyFormFields(
        this.rolesSubject
          .getValue()
          .filter(role => role.name.startsWith('DL_'))
          .sort((a, b) => a.description.localeCompare(b.description))
      );
    } else if (this.isTelekomUser && this.isRequestForOwnUser) {
      this.formFields = this.getLegacyFormFields(
        this.rolesSubject
          .getValue()
          .filter(
            role =>
              role.name.startsWith('REM_') ||
              role.name.startsWith('DTAG_') ||
              role.name.startsWith('IT_')
          )
          .sort((a, b) => a.description.localeCompare(b.description))
      );
    }
  }

  reset() {
    this.router.navigateByUrl('dashboard');
  }

  override validateForm() {
    if (!this.useLegacyUi) {
      super.validateForm();
      return;
    }

    const requestedRoles = this.form.value['userData'].roles.map(x => x.id);
    const userRoles = this.currentRoles.map(x => x.id);
    if (
      requestedRoles.length > 0 &&
      sameArrayMembers(requestedRoles, userRoles)
    ) {
      this.loading = false;
      this.toastr.error(
        this.translateService.instant(
          'user.request.permissions.error_roles_assigned'
        )
      );
      return;
    }
    super.validateForm();
  }

  onLegacyToggleChange() {
    this.formFields[0].form.get('userData.roles').setValue([]);
    this.form.markAsUntouched();
    if (this.isRequestForOwnUser) {
      this.formFields[0].form.get('userData.email').disable();
      this.formFields[0].form
        .get('userData.email')
        .setValue(this.currentUserTokenClaims.email);
      this.currentRoles = this.currentUser.roles;

      if (this.isTelekomUser) {
        this.formFields = this.getLegacyFormFields(
          this.rolesSubject
            .getValue()
            .filter(
              role =>
                role.name.startsWith('REM_') ||
                role.name.startsWith('DTAG_') ||
                role.name.startsWith('IT_')
            )
            .sort((a, b) => a.description.localeCompare(b.description))
        );
      } else {
        this.formFields = this.getLegacyFormFields(
          this.rolesSubject
            .getValue()
            .filter(role => role.name.startsWith('DL_'))
            .sort((a, b) => a.description.localeCompare(b.description))
        );
      }
    } else {
      this.formFields[0].form.get('userData.email').enable();
      this.formFields[0].form.get('userData.email').setValue(null);
      this.formFields[0].form.get('userData.roles').setValue(null);
      this.currentRoles = [];

      if (this.isTelekomUser) {
        this.formFields = this.getLegacyFormFields(
          this.rolesSubject
            .getValue()
            .sort((a, b) => a.description.localeCompare(b.description)),
          []
        );
      } else {
        this.formFields = this.getLegacyFormFields(
          this.rolesSubject
            .getValue()
            .filter(role => role.name.startsWith('DL_'))
            .sort((a, b) => a.description.localeCompare(b.description)),
          []
        );
      }
    }
  }

  getLegacyFormFields(
    roles: RoleViewModel[],
    currentRoles?: RoleViewModel[]
  ): FormlyFieldConfig[] {
    this.currentRoles = this.currentRoles.filter(cr =>
      roles.find(role => role.id === cr.id)
    );
    return [
      {
        type: 'flex-layout',
        key: 'userData',
        fieldGroup: [
          {
            key: 'isRequestForOwnUser',
            type: 'select',
            defaultValue: true,
            props: {
              options: [
                {
                  id: 1,
                  label: this.translateService.instant(
                    'user.request.permissions.own_user'
                  ),
                  value: true,
                },
                {
                  id: 2,
                  label: this.translateService.instant(
                    'user.request.permissions.external_user'
                  ),
                  value: false,
                },
              ],
            },
            hooks: {
              onInit: field => {
                field.formControl.valueChanges.subscribe(value => {
                  this.isRequestForOwnUser = value;
                  this.onLegacyToggleChange();
                });
              },
            },
          },
          {
            key: 'email',
            type: 'input',
            defaultValue: this.isRequestForOwnUser
              ? this.currentUserTokenClaims.email
              : '',
            props: {
              label: this.translateService.instant(
                'user.request.permissions.label__email'
              ),
              required: true,
              disabled: this.isRequestForOwnUser,
              pattern: RegexpEmail,
            },
            validation: {
              messages: {
                pattern: (_err, _field: FormlyFieldConfig) =>
                  this.translateService.instant('formErrors.email.pattern'),
              },
            },
          },
          {
            key: 'roles',
            type: 'autocomplete-chips-for-roles',
            defaultValue: currentRoles ?? this.currentRoles,
            props: {
              label: this.translateService.instant(
                'user.request.permissions.label__roles'
              ),
              options: roles,
              currentRoles: currentRoles ?? this.currentRoles,
              disablePreselectedRoles: false,
            },
            validation: {
              messages: {
                required: this.translateService.instant('formErrors.required', {
                  label: this.translateService.instant(
                    'user.request.permissions.label__roles'
                  ),
                }),
              },
            },
          },
        ],
      },
    ];
  }

  requestRoles() {
    this.loadingMessage = this.translateService.instant('form.loading_message');
    this.loading = true;
    const value = this.form.getRawValue() as any;

    let requestObsv: Observable<IncidentIdResult>;

    const roles = value.userData.roles.map((x: RoleViewModel) => x.id);

    if (this.isRequestForOwnUser) {
      requestObsv = this.userAdministrationClient.requestOwnRoles({ roles });
    } else {
      const request: RoleIncidentRequest = {
        email: value.userData.email,
        roles,
      };

      requestObsv = this.userAdministrationClient.requestRoles(request);
    }

    requestObsv.subscribe({
      next: (res: IncidentIdResult) => {
        this.loading = false;
        if (res?.ticketNumber) {
          const dialogSuccessMessage = `${this.successMessage}${this.translateService.instant(
            'ticket.create.success.message_ticketNumber_addition'
          )}${res.ticketNumber}`;
          this.openSuccessDialog(dialogSuccessMessage);
        } else {
          this.openSuccessDialog();
        }
      },
      error: error => {
        this.loading = false;
        const errTitle = this.translateService.instant(
          'ticket.create.error.title'
        );
        let errorMessage = '';

        if (
          error.errorCode ===
          SmartPortalProcessError.ROLES_ALREADY_ASSIGNED_OR_REQUESTED
        ) {
          errorMessage = this.translateService.instant(
            'user.request.permissions.error_roles_assigned_or_requested'
          );
        } else if (
          error.errorCode === SmartPortalProcessError.ROLES_ALREADY_REQUESTED
        ) {
          errorMessage = this.translateService.instant(
            'user.request.permissions.error_roles_requested'
          );
        } else {
          errorMessage = this.translateService.instant(
            'user.request.permissions.errorMessage'
          );
        }

        this.openErrorDialog(errTitle, errorMessage);
      },
    });
  }

  // ===========================================================================
  // Variante mit Button, Modal und Rollen-Tabelle (useLegacyUi === false)
  // ===========================================================================

  private initAssignedRolesColumns(): void {
    const columns: MtxGridColumn[] = [
      {
        header: this.translateService.instant(
          'user.request.permissions.assignment.table.username'
        ),
        field: 'username',
        sortable: true,
        class: 'truncated',
        resizable: true,
      },
      {
        header: this.translateService.instant(
          'user.request.permissions.assignment.table.email'
        ),
        field: 'email',
        sortable: true,
        class: 'truncated',
        resizable: true,
      },
      {
        header: this.translateService.instant(
          'user.request.permissions.assignment.table.role'
        ),
        field: 'roleName',
        sortable: true,
        class: 'truncated',
        resizable: true,
      },
    ];

    // Ohne Befristung haetten die Gueltigkeitsspalten dauerhaft nur "--" zu
    // zeigen, und verlaengern laesst sich eine unbefristete Rolle nicht.
    if (!this.isTelekomTarget()) {
      columns.push(
        {
          header: this.translateService.instant(
            'user.request.permissions.assignment.table.validFrom'
          ),
          field: 'validFrom',
          sortable: true,
          class: 'truncated',
          formatter: (data: UserRoleAssignmentRow) =>
            data.validFrom
              ? moment(data.validFrom)
                  .locale(this.translateService.instant('locale.locale'))
                  .format(
                    this.translateService.instant(
                      'locale.moment__date_format_only_digits'
                    )
                  )
              : '--',
          resizable: true,
        },
        {
          header: this.translateService.instant(
            'user.request.permissions.assignment.table.validUntil'
          ),
          field: 'validUntil',
          sortable: true,
          class: 'truncated',
          formatter: (data: UserRoleAssignmentRow) =>
            data.validUntil
              ? moment(data.validUntil)
                  .locale(this.translateService.instant('locale.locale'))
                  .format(
                    this.translateService.instant(
                      'locale.moment__date_format_only_digits'
                    )
                  )
              : '--',
          resizable: true,
        }
      );
    }

    // Verlaengern und Beenden laufen ueber Endpunkte der Benutzeradministration
    // und sind ohne deren Admin-Rolle nicht erlaubt.
    if (this.canManageRoleAssignments) {
      if (!this.isTelekomTarget()) {
        columns.push({
          header: this.translateService.instant(
            'user.request.permissions.assignment.table.extend'
          ),
          field: 'extend',
          cellTemplate: this.extendTemplateRef,
          resizable: true,
        });
      }

      columns.push({
        header: this.translateService.instant(
          'user.request.permissions.assignment.table.end'
        ),
        field: 'end',
        cellTemplate: this.endTemplateRef,
        resizable: true,
      });
    }

    this.assignedRolesColumns = columns;
  }

  onSelectPerson(person: EmploymentSearchResultDto | ContactViewModel) {
    this.selectedEmployee = toEmployee(person);
    this.selectedEmployeeId = this.selectedEmployee?.id;
    this.selectedB2cUserId = null;
    this.selectedUser = null;
    this.assignedRoles = [];
    this.setSelectedUserIsTelekomEmployee(false);

    // Keine der beiden Suchen kennt die Verzeichnis-Id: die Mitarbeitersuche
    // liefert die Personalnummer aus dem HR-System, die Kontaktsuche die
    // Contact-Id aus Dynamics. Gemeinsamer Schluessel aller Systeme ist nur
    // die E-Mail-Adresse.
    const email = this.selectedEmployee?.email;

    if (!this.isRequestForOwnUser && email) {
      this.userClient.getByEmail(email).subscribe({
        next: user => {
          this.selectedB2cUserId = user?.id;
          this.selectedUser = user;
          this.setSelectedUserIsTelekomEmployee(user?.isTelekomEmployee);
          this.assignedRoles = this.mapUserRolesToRows(
            user,
            this.selectedEmployee
          );
        },
        error: () => {
          this.assignedRoles = [];
        },
      });
    }
  }

  /**
   * Uebernimmt die Zugehoerigkeit des ausgewaehlten Nutzers und baut die
   * Spalten neu auf, da sie davon abhaengen.
   */
  private setSelectedUserIsTelekomEmployee(isTelekomEmployee: boolean): void {
    this.selectedUserIsTelekomEmployee = isTelekomEmployee ?? false;
    this.initAssignedRolesColumns();
  }

  onDeletePerson() {
    this.selectedEmployeeId = null;
    this.selectedEmployee = null;
    this.selectedB2cUserId = null;
    this.selectedUser = null;
    this.assignedRoles = [];
    this.setSelectedUserIsTelekomEmployee(false);
  }

  /**
   * Aktuelle und bereits abgelaufene Rollen stehen gemeinsam in der Tabelle,
   * damit auch abgelaufene Vergaben verlaengert werden koennen. Vorzeitig
   * entzogene Rollen enden am Tag des Entzugs und gelten daher ueber ihre
   * Herkunft als abgelaufen, nicht ueber das Datum.
   */
  private mapUserRolesToRows(
    user: UserViewModel,
    employee: EmploymentSearchResultDto
  ): UserRoleAssignmentRow[] {
    const expiredRoleIds = new Set(
      (user?.expiredRoles ?? []).map(role => role.id)
    );

    return [...(user?.roles ?? []), ...(user?.expiredRoles ?? [])].map(
      role => ({
        id: `${user?.id}-${role.id}`,
        employeeId: employee?.id,
        b2cUserId: user?.id,
        username: employee?.fullName,
        email: employee?.email,
        roleId: role.id,
        roleName: role.description,
        validFrom: role.validFrom,
        validUntil: role.validUntil,
        isExpired:
          expiredRoleIds.has(role.id) || isRoleExpired(role.validUntil),
      })
    );
  }

  /**
   * Gehoert die Person, um deren Berechtigungen es geht, zur Telekom? Beim
   * eigenen Nutzer ist das die eigene Zugehoerigkeit. Bei einer fremden Person
   * zaehlt ihr Verzeichnis-Eintrag, solange es ihn gibt - das ist der Wert, mit
   * dem auch das Backend arbeitet. Existiert noch keiner, zaehlt die Quelle,
   * aus der die Person gewaehlt wurde.
   */
  private isTelekomTarget(): boolean {
    if (this.isRequestForOwnUser || this.selectedB2cUserId) {
      return this.selectedUserIsTelekomEmployee;
    }

    return this.isTelekomPersonSearch;
  }

  private getAssignableRoles(): RoleViewModel[] {
    return this.rolesSubject
      .getValue()
      .filter(role =>
        this.isTelekomTarget()
          ? role.name.startsWith('REM_') ||
            role.name.startsWith('DTAG_') ||
            role.name.startsWith('IT_')
          : role.name.startsWith('DL_')
      )
      .sort((a, b) => a.description.localeCompare(b.description));
  }

  openAssignRoleDialog() {
    if (!this.selectedEmployee) {
      return;
    }

    const dialogRef = this.roleAssignmentDialog.open(AssignUserRoleComponent, {
      panelClass: 'smartportal-dialog',
      width: '800px',
      data: {
        employee: this.selectedEmployee,
        roles: this.getAssignableRoles(),
        isRequestForOwnUser: this.isRequestForOwnUser,
        isTelekomEmployee: this.isTelekomTarget(),
      },
    });

    dialogRef.afterClosed().subscribe({
      next: (result: AssignUserRoleDialogResult) => {
        if (!result) {
          return;
        }

        const newRows: UserRoleAssignmentRow[] = result.roles.map(role => ({
          id: `${this.selectedB2cUserId}-${role.id}`,
          employeeId: this.selectedEmployee.id,
          b2cUserId: this.selectedB2cUserId,
          username: this.selectedEmployee.fullName,
          email: this.selectedEmployee.email,
          roleId: role.id,
          roleName: role.description,
          validFrom: result.validFrom,
          validUntil: result.validUntil,
        }));

        this.assignedRoles = [...this.assignedRoles, ...newRows];

        this.toastr.success(
          this.translateService.instant(
            'user.request.permissions.assignment.toastr.assign_success'
          )
        );
      },
    });
  }

  onExtendRole(row: UserRoleAssignmentRow) {
    const dialogRef = this.roleAssignmentDialog.open(ExtendUserRoleComponent, {
      panelClass: 'smartportal-dialog',
      width: '800px',
      data: { row },
    });

    dialogRef.afterClosed().subscribe({
      next: (extendDate: Date) => {
        if (!extendDate) {
          return;
        }

        this.submitRoleAssignmentChange(
          this.userAdministrationClient.extendRole({
            b2cUserId: row.b2cUserId,
            roleId: row.roleId,
            validUntil: toDateOnlyString(extendDate) as unknown as Date,
          }),
          'user.request.permissions.assignment.toastr.extend_success'
        );
      },
    });
  }

  onEndRole(row: UserRoleAssignmentRow) {
    const dialogRef = this.roleAssignmentDialog.open(EndUserRoleComponent, {
      panelClass: 'smartportal-dialog',
      width: '800px',
      data: { row },
    });

    dialogRef.afterClosed().subscribe({
      next: (confirmed: boolean) => {
        if (!confirmed) {
          return;
        }

        // Wie in der Benutzeradministration: der Nutzer wird ohne die Rolle
        // gespeichert, den Entzug leitet das Backend daraus ab.
        const user = this.selectedUser;
        const remainingRoles = (user.roles ?? []).filter(
          role => role.id !== row.roleId
        );

        this.submitRoleAssignmentChange(
          this.userAdministrationClient.updateUser({
            firstName: user.firstName,
            lastName: user.lastName,
            email: user.email,
            companyId: null,
            b2cUserId: user.id,
            contactId: user.contactId,
            roles: remainingRoles.map(role => role.id),
            roleValidities: toRoleValidityRequests(remainingRoles),
          }),
          'user.request.permissions.assignment.toastr.end_success'
        );
      },
    });
  }

  /**
   * Nur Rollen, die der Nutzer hat oder bis zu ihrem Ablauf hatte, lassen sich
   * verlaengern oder beenden. Frisch im Dialog zugewiesene Zeilen sind noch
   * nicht gespeichert.
   */
  isPersistedRole(row: UserRoleAssignmentRow): boolean {
    return [
      ...(this.selectedUser?.roles ?? []),
      ...(this.selectedUser?.expiredRoles ?? []),
    ].some(role => role.id === row.roleId);
  }

  /** Hebt abgelaufene Vergaben in der Tabelle hervor. */
  readonly assignedRoleRowClassFormatter = {
    'role-row--expired': (row: UserRoleAssignmentRow) => row.isExpired === true,
  };

  /**
   * Sendet eine Aenderung an einer bestehenden Rollenvergabe. Das Backend legt
   * dafuer ein Ticket an, die Vergabe selbst aendert sich erst mit dessen
   * Bearbeitung - deshalb bleibt die Tabelle unveraendert.
   */
  private submitRoleAssignmentChange(
    request: Observable<IncidentIdResult>,
    successMessageKey: string
  ): void {
    this.loadingMessage = this.translateService.instant('form.loading_message');
    this.loading = true;

    request.subscribe({
      next: (res: IncidentIdResult) => {
        this.loading = false;
        const message = this.translateService.instant(successMessageKey);
        this.openSuccessDialog(
          res?.ticketNumber
            ? `${message}${this.translateService.instant(
                'ticket.create.success.message_ticketNumber_addition'
              )}${res.ticketNumber}`
            : message
        );
      },
      error: error => {
        this.loading = false;
        this.openErrorDialog(
          this.translateService.instant('ticket.create.error.title'),
          this.translateService.instant(
            error?.errorCode === SmartPortalProcessError.ROLES_ALREADY_REQUESTED
              ? 'user.request.permissions.error_roles_requested'
              : 'user.request.permissions.errorMessage'
          )
        );
      },
    });
  }

  /**
   * Wechsel des Suchfilters. Die bisherige Auswahl stammt aus der anderen
   * Quelle und wird deshalb verworfen, danach werden die Felder neu gebaut.
   */
  onPersonSearchTypeChange(isTelekomPersonSearch: boolean) {
    this.isTelekomPersonSearch = isTelekomPersonSearch;
    this.onDeletePerson();

    const personModel = (this.formModel as any)?.userData?.person;
    if (personModel) {
      personModel.searchResult = null;
    }

    this.formFields = this.getFormFields();
  }

  onToggleChange() {
    this.selectedEmployeeId = null;
    this.selectedEmployee = null;
    this.selectedB2cUserId = null;
    this.selectedUser = null;
    this.assignedRoles = [];
    this.setSelectedUserIsTelekomEmployee(false);

    const personModel = (this.formModel as any)?.userData?.person;
    if (personModel) {
      personModel.searchResult = null;
    }

    if (this.isRequestForOwnUser) {
      // id bleibt hier leer: das ist die Personalnummer aus der Personensuche,
      // die fuer den eigenen Nutzer nicht vorliegt. Die Verzeichnis-Id steht
      // in selectedB2cUserId.
      this.selectedEmployee = {
        fullName: [this.currentUser?.firstName, this.currentUser?.lastName]
          .filter(Boolean)
          .join(' '),
        email:
          this.currentUser?.email ??
          (this.currentUserTokenClaims?.email as string),
      };
      this.selectedB2cUserId = this.currentUser?.id;
      this.selectedUser = this.currentUser;
      this.setSelectedUserIsTelekomEmployee(
        this.currentUser?.isTelekomEmployee
      );

      this.assignedRoles = this.mapUserRolesToRows(
        this.currentUser,
        this.selectedEmployee
      );
    }

    this.formFields = this.getFormFields();
  }

  getFormFields(): FormlyFieldConfig[] {
    const fieldGroup: FormlyFieldConfig[] = [
      {
        key: 'isRequestForOwnUser',
        type: 'select',
        defaultValue: true,
        props: {
          // Das Feld hat kein sichtbares Label, Screenreader brauchen den Zweck.
          attributes: {
            'aria-label': this.translateService.instant(
              'user.request.permissions.aria__request_target'
            ),
          },
          options: [
            {
              id: 1,
              label: this.translateService.instant(
                'user.request.permissions.own_user'
              ),
              value: true,
            },
            {
              id: 2,
              label: this.translateService.instant(
                'user.request.permissions.external_user'
              ),
              value: false,
            },
          ],
        },
        hooks: {
          onInit: field => {
            field.formControl.valueChanges.subscribe(value => {
              this.isRequestForOwnUser = value;
              this.onToggleChange();
            });
          },
        },
      },
    ];

    fieldGroup.push({
      key: 'isTelekomPersonSearch',
      type: 'select',
      defaultValue: this.isTelekomPersonSearch,
      props: {
        disabled: this.isRequestForOwnUser,
        attributes: {
          'aria-label': this.translateService.instant(
            'user.request.permissions.aria__person_group'
          ),
        },
        options: [
          {
            id: 1,
            label: this.translateService.instant(
              'administration.create_user.telekom_employee'
            ),
            value: true,
          },
          {
            id: 2,
            label: this.translateService.instant(
              'administration.create_user.external_user'
            ),
            value: false,
          },
        ],
      },
      hooks: {
        onInit: field => {
          field.formControl.valueChanges.subscribe(value => {
            this.onPersonSearchTypeChange(value);
          });
        },
      },
    });

    const placeholder = this.translateService.instant(
      'user.request.permissions.assignment.filter_person_title'
    );

    // Telekom-Mitarbeitende stehen in der Mitarbeitersuche, Externe in der
    // Kontaktsuche. Beide melden den Treffer als drittes Argument von onSelect.
    const personSearchFields = this.isTelekomPersonSearch
      ? this.employeeFormlyFieldService.autocompleteSearch(
          placeholder,
          (_id, _initialSearch, value) => this.onSelectPerson(value),
          false,
          false,
          null,
          true,
          () => this.onDeletePerson(),
          undefined,
          this.translateService.instant(
            'user.request.permissions.assignment.deletePerson_tooltip'
          )
        )
      : this.contactFormlyFieldService.autocompleteSearch(
          placeholder,
          (_id, _initialSearch, value) => this.onSelectPerson(value),
          false,
          false,
          null,
          true,
          () => this.onDeletePerson()
        );

    // Der Screenreader nennt mit, in welcher Personengruppe gesucht wird.
    const personGroupLabel = this.translateService.instant(
      this.isTelekomPersonSearch
        ? 'administration.create_user.telekom_employee'
        : 'administration.create_user.external_user'
    );

    personSearchFields[0].props = {
      ...personSearchFields[0].props,
      disabled: this.isRequestForOwnUser,
      ariaLabel: `${placeholder} (${personGroupLabel})`,
    };

    fieldGroup.push({
      key: 'person',
      fieldGroup: personSearchFields,
    });

    return [
      {
        type: 'flex-layout',
        key: 'userData',
        fieldGroup,
      },
    ];
  }
}
